import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Award, Globe, CheckCircle2 } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Client-First Approach",
      description: "Every recommendation is made with the client's best interests at heart."
    },
    {
      icon: Users,
      title: "Education Over Sales",
      description: "We believe informed clients make better decisions for their families."
    },
    {
      icon: Award,
      title: "Excellence in Service",
      description: "We partner only with top-rated carriers and maintain the highest standards."
    },
    {
      icon: Globe,
      title: "Cultural Understanding",
      description: "Deep appreciation for diverse family values and financial goals."
    }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="about-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 block">
              Our Story
            </span>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              About ShopCompareInsure
            </h1>
            <p className="text-slate-300 text-xl mb-8">
              We're building a modern financial protection platform that serves two important groups: families seeking insurance coverage and individuals seeking rewarding careers.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">Our Mission</h2>
              <p className="text-slate-400 mb-6">
                ShopCompareInsure exists to democratize access to quality life insurance advice and create economic opportunity for those who want to help others protect their families.
              </p>
              <p className="text-slate-400">
                We combine cutting-edge technology with personalized advisory service to deliver better outcomes for both clients and advisors.
              </p>
            </div>
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">What We Believe</h2>
              <ul className="space-y-4">
                {[
                  "Everyone deserves access to quality financial guidance",
                  "Transparency builds trust and better outcomes",
                  "Technology should enhance, not replace, human connection",
                  "Success comes from helping others succeed"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059] mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl text-white mb-12 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div key={idx} className="bg-slate-900/50 border border-slate-800 p-6 text-center hover:border-[#C5A059]/30 transition-colors">
                  <div className="w-14 h-14 rounded-full bg-[#C5A059]/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{value.title}</h3>
                  <p className="text-slate-400 text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Join Us</h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Whether you're looking for insurance protection or a career opportunity, we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/compare"
              className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
            >
              Get Insurance Quote
            </Link>
            <Link
              to="/become-an-agent"
              className="inline-flex items-center justify-center border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10 px-8 py-4 uppercase tracking-wider text-sm transition-all"
            >
              Become an Advisor
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
